import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  getAbsolutePlayerTimePure,
  getPlayerTimeFromAbsoluteTimePure
} from '@goldcast/player-config/src/utils/timeUtils';
import { getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { useDragElement } from '../hooks/useDragElement';
import { zoomLevelStore } from '../store/zoomLevel';
import { MIN_SELECTION_SIZE, TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '../constants';
import {
  getAbsoluteTimeFromSegmentTime,
  getPlayerTimeFromSegmentTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromAbsoluteTime,
  MIN_SEGMENT_WIDTH
} from '../timeUtils';
import { ADD_MENU_POPOVER_TEST_ID, AUDIO_CONTROL_POPOVER_TEST_ID } from '../TimelinePlayerActionBar/AddMenu/constants';
import CreateHookButton from './CreateHookButton';
import useCreateHookFromSelection from './useCreateHookFromSelection';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import useThrottle from '@/hooks/useThrottle';
import { getClipTotalDurationInFrames } from '@/App/remotion/MainPlayer/utils/configUtils';

export default function FullTimelineSelect({
  speakerTimelineContainerRef,
  speakerTimelineRef
}: {
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineRef: React.RefObject<HTMLDivElement>;
}) {
  const { clipData, fps, clipId } = useClipsContext();

  const [isCreatingSelection, setIsCreatingSelection] = useState(false);
  const dragInfoRef = useRef<{
    time: number | null;
    segmentId: string | null;
  }>({
    time: null,
    segmentId: null
  });
  const selectionContainerRef = useRef<HTMLDivElement>(null);
  const [selectionContainerWidth, setSelectionContainerWidth] = useState(0);
  const { seekTo } = useRemotionPlayerSeek();
  const { startTime, endTime, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );
  useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);

  const selectionPlayerStartTime =
    startTime !== null && !!startSegmentId ? getAbsoluteTimeFromSegmentTime(startTime, clipData, startSegmentId) : null;
  const selectionPlayerEndTime =
    !!endTime && !!endSegmentId ? getAbsoluteTimeFromSegmentTime(endTime, clipData, endSegmentId) : null;
  const selectionWidth =
    selectionPlayerStartTime !== null && selectionPlayerEndTime !== null
      ? Math.abs(selectionPlayerStartTime - selectionPlayerEndTime)
      : 0;
  const selectionLeft =
    selectionPlayerStartTime !== null && selectionPlayerEndTime !== null ? selectionPlayerStartTime : 0;

  const throttledForceScrollEvent = useThrottle(
    (time: number, segmentId) =>
      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time,
        segmentId
      }),
    100
  );

  const { onMouseDown } = useDragElement({
    setDraggingElement: (dragId: string | null) => {
      setIsCreatingSelection(!!dragId);
    },
    setDraggingElementOffset: (offsetTime: number) => {
      const { startTime, startSegmentId } = transcriptTimelineSelectionStore.getSnapshot();
      if (
        startTime === null ||
        startSegmentId === null ||
        dragInfoRef.current.time === null ||
        dragInfoRef.current.segmentId === null
      ) {
        dragInfoRef.current = {
          time: null,
          segmentId: null
        };
        return;
      }

      const newSelectionAbsoluteStartTime = getAbsoluteTimeFromSegmentTime(
        dragInfoRef.current.time,
        clipData,
        dragInfoRef.current.segmentId
      );

      const newSelectionAbsoluteEndTime = newSelectionAbsoluteStartTime + offsetTime;
      const newSelectionPlayerEndTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: newSelectionAbsoluteEndTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const newSelectionEndSegment = getSegmentAtCurrentFrame(newSelectionPlayerEndTime, clipData);
      if (Math.abs(offsetTime * fps * zoomLevelStore.getSnapshot().zoomLevel) < MIN_SELECTION_SIZE) {
        transcriptTimelineSelectionStore.set(state => ({
          ...state,
          endTime: null,
          endSegmentId: null,
          isSelecting: false
        }));
        return;
      }

      if (!newSelectionEndSegment) {
        return;
      }
      let newSelectionEndSegmentTime = getSegmentTimeFromAbsoluteTime(
        newSelectionAbsoluteEndTime,
        clipData,
        newSelectionEndSegment.id
      );

      newSelectionEndSegmentTime =
        roundToNDecimalPlaces(newSelectionEndSegmentTime, 3) === 0 ? 0 : newSelectionEndSegmentTime;

      const isReverseDirection = offsetTime < 0;

      throttledForceScrollEvent(newSelectionEndSegmentTime, newSelectionEndSegment.id);
      transcriptTimelineSelectionStore.update(state => ({
        ...state,
        startTime: isReverseDirection ? newSelectionEndSegmentTime : dragInfoRef.current.time,
        startSegmentId: isReverseDirection ? newSelectionEndSegment.id : dragInfoRef.current.segmentId,
        endTime: isReverseDirection ? dragInfoRef.current.time : newSelectionEndSegmentTime,
        endSegmentId: isReverseDirection ? dragInfoRef.current.segmentId : newSelectionEndSegment.id
      }));
    },
    onDragEnd: (offsetTime: number) => {
      const {
        startTime: latestStartTime,
        endTime: latestEndTime,
        startSegmentId: latestStartSegmentId,
        endSegmentId: latestEndSegmentId
      } = transcriptTimelineSelectionStore.getSnapshot();
      if (
        latestStartTime === null ||
        latestEndTime === null ||
        latestStartSegmentId === null ||
        latestEndSegmentId === null
      ) {
        transcriptTimelineSelectionStore.set(() => ({ ...defaultTimelineSelectionState }));
        return;
      }
      setIsCreatingSelection(false);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));

      if (Math.abs(offsetTime * fps * zoomLevelStore.getSnapshot().zoomLevel) < MIN_SELECTION_SIZE) {
        transcriptTimelineSelectionStore.set(() => ({
          ...defaultTimelineSelectionState
        }));
        dragInfoRef.current = {
          time: null,
          segmentId: null
        };
        return;
      }
      transcriptTimelineSelectionStore.update(state => ({
        ...state,
        isSelecting: false
      }));
      dragInfoRef.current = {
        time: null,
        segmentId: null
      };

      const playerTime = getPlayerTimeFromSegmentTime(latestStartTime, clipData, latestStartSegmentId);
      seekTo(playerTime, false);
    },
    validDropZoneRef: selectionContainerRef,
    droppableContainerRef: speakerTimelineRef,
    preventResetOffsetOnDragEnd: true,
    fps
  });

  useLayoutEffect(() => {
    setSelectionContainerWidth(selectionContainerRef.current?.offsetWidth ?? 0);
  }, [startTime, endTime, isCreatingSelection]);

  const { isAddHookVisible, handleCreateHookSegment } = useCreateHookFromSelection({
    clipData,
    clipId,
    startTime,
    endTime,
    startSegmentId,
    endSegmentId,
    isCreatingSelection,
    selectionContainerWidth
  });

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (
        !document
          .getElementsByClassName(TIMELINE_COMPONENT_CLASS_IDENTIFIER.FIXED_WIDTH_TIMELINE)[0]
          ?.contains(e.target as Node) ||
        document
          .getElementsByClassName(TIMELINE_COMPONENT_CLASS_IDENTIFIER.CHAPTER_CONTAINER)[0]
          ?.contains(e.target as Node) ||
        document
          .getElementsByClassName(TIMELINE_COMPONENT_CLASS_IDENTIFIER.ELEMENT_MENU_CONTAINER)[0]
          ?.contains(e.target as Node) ||
        (e.target as Element).closest(`[data-testid="${ADD_MENU_POPOVER_TEST_ID}"]`) ||
        (e.target as Element).closest(`#${AUDIO_CONTROL_POPOVER_TEST_ID}`) ||
        (e.target as Element).closest('[role="button"]')
      ) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      const rect = speakerTimelineRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
      const minOffset = (rect.left - e.clientX) / zoomLevel / fps;
      const maxOffset = (rect.right - e.clientX) / zoomLevel / fps;
      const currentAbsoluteTime = (e.clientX - rect.left) / zoomLevel / fps;
      const startTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: currentAbsoluteTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const currentSegment = getSegmentAtCurrentFrame(startTime, clipData);
      if (!currentSegment) {
        return;
      }
      const segmentTime = getSegmentTimeFromAbsoluteTime(currentAbsoluteTime, clipData, currentSegment.id);

      dragInfoRef.current = {
        time: segmentTime,
        segmentId: currentSegment.id
      };
      transcriptTimelineSelectionStore.update(() => ({
        ...defaultTimelineSelectionState,
        startTime: segmentTime,
        startSegmentId: currentSegment.id,
        isSelecting: true
      }));

      onMouseDown(e, 'timeline-select', maxOffset, minOffset);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [clipData.asset_metadata.player_config, onMouseDown, fps, speakerTimelineRef, clipData]);

  const { onMouseDown: onLeftDragStart } = useDragElement({
    setDraggingElement: () => {
      const { startTime, startSegmentId } = transcriptTimelineSelectionStore.getSnapshot();

      if (startTime === null || startSegmentId === null) {
        return;
      }
      dragInfoRef.current = {
        time: startTime,
        segmentId: startSegmentId
      };
    },
    setDraggingElementOffset: (offsetTime: number) => {
      const { startTime, startSegmentId } = transcriptTimelineSelectionStore.getSnapshot();
      if (
        startTime === null ||
        startSegmentId === null ||
        dragInfoRef.current.time === null ||
        dragInfoRef.current.segmentId === null
      ) {
        dragInfoRef.current = {
          time: null,
          segmentId: null
        };
        return;
      }

      const oldSelectionAbsoluteStartTime = getAbsoluteTimeFromSegmentTime(
        dragInfoRef.current.time,
        clipData,
        dragInfoRef.current.segmentId
      );

      const newSelectionAbsoluteStartTime = oldSelectionAbsoluteStartTime + offsetTime;
      const newSelectionPlayerStartTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: newSelectionAbsoluteStartTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const newSelectionStartSegment = getSegmentAtCurrentFrame(newSelectionPlayerStartTime, clipData);

      if (!newSelectionStartSegment) {
        return;
      }
      const newSelectionStartTime = getSegmentTimeFromAbsoluteTime(
        newSelectionAbsoluteStartTime,
        clipData,
        newSelectionStartSegment.id
      );

      throttledForceScrollEvent(newSelectionStartTime, newSelectionStartSegment.id);
      transcriptTimelineSelectionStore.update(state => ({
        ...state,
        startTime: newSelectionStartTime,
        startSegmentId: newSelectionStartSegment.id
      }));
    },
    onDragEnd: () => {
      const startTime = transcriptTimelineSelectionStore.getSnapshot().startTime;
      const startSegmentId = transcriptTimelineSelectionStore.getSnapshot().startSegmentId;
      if (startTime === null || startSegmentId === null) {
        return;
      }
      const playerTime = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
      seekTo(playerTime);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));
    },
    validDropZoneRef: selectionContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    preventResetOffsetOnDragEnd: true,
    fps
  });
  const { onMouseDown: onRightDragStart } = useDragElement({
    setDraggingElement: () => {
      const endTime = transcriptTimelineSelectionStore.getSnapshot().endTime;
      if (endTime === null) {
        return;
      }
      dragInfoRef.current = {
        time: endTime,
        segmentId: endSegmentId
      };
    },
    setDraggingElementOffset: (offsetTime: number) => {
      const { endTime, endSegmentId } = transcriptTimelineSelectionStore.getSnapshot();
      if (
        endTime === null ||
        endSegmentId === null ||
        dragInfoRef.current.time === null ||
        dragInfoRef.current.segmentId === null
      ) {
        dragInfoRef.current = {
          time: null,
          segmentId: null
        };
        return;
      }

      const oldSelectionAbsoluteEndTime = getAbsoluteTimeFromSegmentTime(
        dragInfoRef.current.time,
        clipData,
        dragInfoRef.current.segmentId
      );

      const newSelectionAbsoluteEndTime = oldSelectionAbsoluteEndTime + offsetTime;
      const newSelectionPlayerEndTime = getPlayerTimeFromAbsoluteTimePure({
        absoluteTime: newSelectionAbsoluteEndTime,
        clipData: clipData,
        minSegmentWidth: MIN_SEGMENT_WIDTH,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      });
      const newSelectionEndSegment = getSegmentAtCurrentFrame(newSelectionPlayerEndTime, clipData);

      if (!newSelectionEndSegment) {
        return;
      }
      const newSelectionEndSegmentTime = getSegmentTimeFromAbsoluteTime(
        newSelectionAbsoluteEndTime,
        clipData,
        newSelectionEndSegment.id
      );

      throttledForceScrollEvent(newSelectionEndSegmentTime, newSelectionEndSegment.id);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        endTime: newSelectionEndSegmentTime,
        endSegmentId: newSelectionEndSegment.id
      }));
    },
    onDragEnd: () => {
      const endTime = transcriptTimelineSelectionStore.getSnapshot().endTime;
      const endSegmentId = transcriptTimelineSelectionStore.getSnapshot().endSegmentId;
      if (endTime === null || endSegmentId === null) {
        return;
      }
      const playerTime = getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId);
      seekTo(playerTime);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));
    },
    validDropZoneRef: selectionContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    preventResetOffsetOnDragEnd: true,
    fps
  });

  if (startTime === null || endTime === null || startTime === endTime) {
    return;
  }

  return (
    <>
      <CreateHookButton
        isVisible={isAddHookVisible}
        left={`calc(${(selectionLeft + selectionWidth / 2) * fps}px * var(--timeline-zoom) - 16px)`}
        top="5px"
        onCreate={handleCreateHookSegment}
      />
      <div
        className={`pointer-events-none absolute inset-x-0 size-full overflow-hidden`}
        style={{
          top: '0px',
          bottom: '0px'
        }}
      >
        <div
          ref={selectionContainerRef}
          className="group pointer-events-auto absolute z-50 size-full"
          style={{
            left: `calc(${selectionLeft * fps}px * var(--timeline-zoom))`,
            width: `calc(${selectionWidth * fps}px * var(--timeline-zoom) + 2px) `
          }}
        >
          <div className="absolute inset-0 mr-0.5 mt-1 bg-orange-500/20 group-hover:outline group-hover:outline-offset-[-2px] group-hover:outline-orange-500"></div>
          {!isCreatingSelection && (
            <>
              <div
                className="absolute inset-y-0 left-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
                onMouseDown={e => {
                  if (startSegmentId === null || endSegmentId === null) {
                    return;
                  }
                  const startAbsoluteTime = getAbsoluteTimeFromSegmentTime(startTime, clipData, startSegmentId);
                  const endAbsoluteTime = getAbsoluteTimeFromSegmentTime(endTime, clipData, endSegmentId);

                  const minOffset = startAbsoluteTime;
                  const maxOffset = endAbsoluteTime - startAbsoluteTime;
                  transcriptTimelineSelectionStore.set(state => ({
                    ...state,
                    isSelecting: true
                  }));
                  onLeftDragStart(e, 'timeline-select', maxOffset, -minOffset);
                }}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <div className="h-full w-0.5 rounded bg-transparent"></div>
              </div>
              <div
                className="absolute inset-y-0 right-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
                onMouseDown={e => {
                  if (endSegmentId === null || startSegmentId === null) {
                    return;
                  }
                  const endAbsoluteTime = getAbsoluteTimeFromSegmentTime(endTime, clipData, endSegmentId);
                  const startAbsoluteTime = getAbsoluteTimeFromSegmentTime(startTime, clipData, startSegmentId);
                  const playerDuration = getClipTotalDurationInFrames(clipData) / getClipFPS(clipData);
                  const totalAbsoluteTime = getAbsolutePlayerTimePure({
                    time: playerDuration,
                    clipData: clipData,
                    minSegmentWidth: MIN_SEGMENT_WIDTH,
                    zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
                    isSplitsTranslationEnabled:
                      !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
                  });
                  const minOffset = endAbsoluteTime - startAbsoluteTime;
                  const maxOffset = totalAbsoluteTime - endAbsoluteTime;

                  transcriptTimelineSelectionStore.set(state => ({
                    ...state,
                    isSelecting: true
                  }));

                  onRightDragStart(e, 'timeline-select', maxOffset, -minOffset);
                }}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <div className="h-full w-0.5 rounded bg-transparent"></div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
